import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { CourseAnnouncementsPanel } from "@/components/instructor/course-announcements-panel";
import { buttonClasses, Card, Field, PanelSection, Select } from "@/components/ui";
import { fetchCourseEnrollments } from "@/services/api/instructor";
import { ApiDownloadButton } from "@/components/api-download-button";
import type { InstructorEnrollment, InstructorSubmission } from "@/services/api/instructor";

type Course = {
  id: string;
  slug: string;
  title: string;
  status: string;
};

type InstructorDashboardProps = {
  tenantSlug: string;
  courses: Course[];
  initialSubmissions: InstructorSubmission[];
  initialEnrollments: InstructorEnrollment[];
  initialCourseId: string | null;
};

/** A one-line reason the panel is empty, and the link to the place that fills it. */
function EmptyState({
  message,
  actionLabel,
  actionTo,
  testId,
}: {
  message: string;
  actionLabel: string;
  actionTo: string;
  testId: string;
}) {
  return (
    <Card tone="dashed" pad="sm" data-testid={testId}>
      <p className="text-sm text-[var(--text-muted)]">{message}</p>
      <Link
        to={actionTo}
        className={`${buttonClasses({ variant: "secondary", size: "sm" })} mt-3`}
        data-testid={`${testId}-action`}
      >
        {actionLabel}
      </Link>
    </Card>
  );
}

export function InstructorDashboard({
  tenantSlug,
  courses,
  initialSubmissions,
  initialEnrollments,
  initialCourseId,
}: InstructorDashboardProps) {
  const t = useTranslations("instructor");
  // The one selected course: the composer targets it and the enrolment list shows it.
  const [selectedCourseId, setSelectedCourseId] = useState(initialCourseId ?? "");
  const [enrollments, setEnrollments] = useState(initialEnrollments);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [hwCourseFilter, setHwCourseFilter] = useState("");
  const [hwStudentFilter, setHwStudentFilter] = useState("");

  const coursesPath = `/t/${tenantSlug}/instructor/courses`;
  const selectedCourse = courses.find((course) => course.id === selectedCourseId) ?? null;
  const filteredSubmissions = initialSubmissions.filter((row) => {
    if (hwCourseFilter && row.courseId !== hwCourseFilter) return false;
    if (
      hwStudentFilter &&
      !`${row.studentName ?? ""} ${row.studentEmail}`
        .toLowerCase()
        .includes(hwStudentFilter.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  async function handleCourseChange(courseId: string) {
    setSelectedCourseId(courseId);
    setLoadingEnrollments(true);
    try {
      setEnrollments(await fetchCourseEnrollments(courseId));
    } catch {
      setEnrollments([]);
    } finally {
      setLoadingEnrollments(false);
    }
  }

  const courseChooser = selectedCourse ? (
    courses.length === 1 ? (
      <p className="text-sm text-[var(--text-muted)]">
        {t("announcementAudience")}{" "}
        <span
          className="font-semibold text-[var(--text-primary)]"
          data-testid="instructor-announcement-course-name"
        >
          {selectedCourse.title}
        </span>
      </p>
    ) : (
      <Field label={t("announcementAudience")}>
        <Select
          data-testid="instructor-course-select"
          value={selectedCourseId}
          onChange={(e) => void handleCourseChange(e.target.value)}
        >
          {courses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.title}
            </option>
          ))}
        </Select>
      </Field>
    )
  ) : null;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {selectedCourse ? (
          <Card data-testid="instructor-notify-panel">
            <CourseAnnouncementsPanel courseId={selectedCourse.id} courseChooser={courseChooser} />
          </Card>
        ) : (
          <PanelSection
            title={t("announcementsTitle")}
            headingId="instructor-announcements-heading"
            data-testid="instructor-notify-panel-empty"
          >
            <EmptyState
              testId="instructor-no-courses"
              message={t("noCoursesBody")}
              actionLabel={t("buildCourseAction")}
              actionTo={coursesPath}
            />
          </PanelSection>
        )}

        <PanelSection
          title={t("enrollmentsTitle")}
          headingId="instructor-enrollments-heading"
          description={
            selectedCourse
              ? t("enrollmentsForCourse", { course: selectedCourse.title })
              : t("enrollmentsSubtitle")
          }
        >
          {!selectedCourse ? (
            <EmptyState
              testId="instructor-enrollments-empty"
              message={t("noCoursesForEnrollments")}
              actionLabel={t("buildCourseAction")}
              actionTo={coursesPath}
            />
          ) : loadingEnrollments ? (
            <p className="text-sm text-[var(--text-muted)]">{t("loadingEnrollments")}</p>
          ) : enrollments.length === 0 ? (
            selectedCourse.status === "published" ? (
              <EmptyState
                testId="instructor-enrollments-none"
                message={t("noEnrollments", { course: selectedCourse.title })}
                actionLabel={t("reviewPricingAction")}
                actionTo={`${coursesPath}/${selectedCourse.id}#pricing`}
              />
            ) : (
              <EmptyState
                testId="instructor-enrollments-none"
                message={t("noEnrollmentsDraft", { course: selectedCourse.title })}
                actionLabel={t("publishCourseAction")}
                actionTo={`${coursesPath}/${selectedCourse.id}#publish`}
              />
            )
          ) : (
            <ul className="space-y-2" data-testid="instructor-enrollments-list">
              {enrollments.map((row) => (
                <li
                  key={row.enrollmentId}
                  className="rounded-lg border border-[var(--border)] px-4 py-3 text-sm"
                  data-testid={`instructor-enrollment-${row.userId}`}
                >
                  <Link
                    to={`${coursesPath}/${selectedCourseId}/students/${row.userId}`}
                    className="font-medium underline"
                    data-testid={`instructor-enrollment-link-${row.userId}`}
                  >
                    {row.studentName ?? row.studentEmail}
                  </Link>
                  <span className="text-[var(--text-muted)]"> · {row.studentEmail}</span>
                  <span className="text-[var(--text-muted)]"> · {row.cohortName}</span>
                  <span className="text-[var(--text-muted)]"> · {row.status}</span>
                </li>
              ))}
            </ul>
          )}
        </PanelSection>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <PanelSection
          className="lg:col-span-2"
          title={t("homeworkTitle")}
          headingId="instructor-homework-heading"
          description={t("homeworkSubtitle")}
        >
          {initialSubmissions.length > 0 ? (
            <div className="mb-4 flex flex-wrap gap-3">
              <label className="text-sm">
                {t("courseLabel")}
                <select
                  className="ml-2 rounded border border-[var(--border-strong)] bg-[var(--surface-1)] px-2 py-1"
                  value={hwCourseFilter}
                  onChange={(e) => setHwCourseFilter(e.target.value)}
                  data-testid="instructor-hw-course-filter"
                >
                  <option value="">{t("allCourses")}</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm">
                {t("studentFilterLabel")}
                <input
                  className="ml-2 rounded border border-[var(--border-strong)] bg-[var(--surface-1)] px-2 py-1"
                  value={hwStudentFilter}
                  onChange={(e) => setHwStudentFilter(e.target.value)}
                  data-testid="instructor-hw-student-filter"
                  placeholder={t("studentFilterPlaceholder")}
                />
              </label>
            </div>
          ) : null}
          {initialSubmissions.length === 0 ? (
            <EmptyState
              testId="instructor-submissions-empty"
              message={t("noSubmissions")}
              actionLabel={t("addHomeworkAction")}
              actionTo={
                selectedCourse ? `${coursesPath}/${selectedCourse.id}#curriculum` : coursesPath
              }
            />
          ) : filteredSubmissions.length === 0 ? (
            <Card tone="dashed" pad="sm" data-testid="instructor-submissions-filtered-empty">
              <p className="text-sm text-[var(--text-muted)]">{t("noSubmissionsMatch")}</p>
              <button
                type="button"
                className={`${buttonClasses({ variant: "secondary", size: "sm" })} mt-3`}
                onClick={() => {
                  setHwCourseFilter("");
                  setHwStudentFilter("");
                }}
                data-testid="instructor-submissions-clear-filters"
              >
                {t("clearFilters")}
              </button>
            </Card>
          ) : (
            <ul className="space-y-3" data-testid="instructor-submissions-list">
              {filteredSubmissions.map((row) => (
                <li
                  key={row.submissionId}
                  className="rounded-lg border border-[var(--border)] p-4"
                  data-testid={`instructor-submission-${row.submissionId}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium">{row.lessonTitle}</p>
                      <p className="text-sm text-[var(--text-muted)]">
                        {row.courseTitle} ·{" "}
                        <Link
                          to={`${coursesPath}/${row.courseId}/students/${row.studentUserId}`}
                          className="underline"
                          data-testid={`instructor-submission-student-${row.submissionId}`}
                        >
                          {row.studentName ?? row.studentEmail}
                        </Link>
                      </p>
                      <p className="mt-1 text-xs text-[var(--text-muted)]">
                        {new Date(row.submittedAt).toLocaleString()} · {row.fileName}
                      </p>
                    </div>
                    <ApiDownloadButton
                      path={`/v1/instructor/submissions/${row.submissionId}/download`}
                      fileName={row.fileName}
                      className="text-sm text-[var(--text-heading)] underline"
                      data-testid={`instructor-submission-download-${row.submissionId}`}
                    >
                      {t("download")}
                    </ApiDownloadButton>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </PanelSection>

        <section
          className="h-fit rounded-xl border border-[var(--brand-purple)]/15 bg-[var(--brand-surface)] p-5"
          data-testid="instructor-quick-actions"
        >
          <h2 className="text-lg font-semibold">{t("quickActionsTitle")}</h2>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{t("quickActionsHint")}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to={coursesPath}
              data-testid="instructor-courses-link"
              className="rounded-full px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: "var(--accent-solid)" }}
            >
              {t("openBuilder")}
            </Link>
            {selectedCourse ? (
              <>
                <Link
                  to={`${coursesPath}/${selectedCourse.id}#announcements`}
                  data-testid="instructor-notify-link"
                  className="rounded-full border border-[var(--brand-purple)]/20 px-4 py-2 text-sm font-medium"
                >
                  {t("notifyStudents")}
                </Link>
                {selectedCourse.slug && selectedCourse.status === "published" ? (
                  <Link
                    to={`/t/${tenantSlug}/learn/${selectedCourse.slug}/community`}
                    data-testid="instructor-community-link"
                    className="rounded-full border border-[var(--brand-purple)]/20 px-4 py-2 text-sm font-medium"
                  >
                    {t("openCommunity")}
                  </Link>
                ) : null}
              </>
            ) : null}
            <Link
              to={`/t/${tenantSlug}/messages`}
              data-testid="instructor-messages-link"
              className="rounded-full border border-[var(--brand-purple)]/20 px-4 py-2 text-sm font-medium"
            >
              {t("openMessages")}
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
